import { Result } from '@/classes/result';
import { FileType } from '@/enums/files';
import { ErrorMessage } from '@/messages/error';
import { useFileStore } from '@/stores/file';
import type { File, FileContent } from '@/types/file';
import { callApi } from '@/utils/api';
import { processErrorWithFeedback, processErrorWithReturn } from '@/utils/error';
import toast from '@/utils/toast';
import { saveAs } from 'file-saver';

export async function getDirectoryContent(organizationId: string, moduleName: string): Promise<FileContent[]> {
    try {
        const result = await callApi({
            endpoint: `files/directory/${organizationId}/${moduleName}`,
            method: 'GET'
        });

        if (result.status !== 200) {
            if (result.status == 404) {
                return [];
            }

            throw new Error(result.message);
        }
        return result.object;
    } catch (error) {
        return processErrorWithReturn('Mapinhoud kon niet worden opgehaald', [], error);
    }
}

export function getFileName(filePath: string): string {
    const splitPath = filePath.split('/');
    if (splitPath.length === 0) return '';

    return splitPath[splitPath.length - 1] ?? '';
}

export async function downloadFileFromPath(organizationId: string, path: string) {
    try {
        const fileStore = useFileStore();
        const base64WithMetaData = await fileStore.getFile(organizationId, path);

        const blob = await base64toBlob(base64WithMetaData.base64, base64WithMetaData.contentType);
        saveAs(blob, base64WithMetaData.name);
        toast.success('FileDownloaded');
    } catch (error) {
        processErrorWithFeedback('Fout opgetreden tijdens het proberen te downloaden van een document', error);
    }
}

export async function base64toBlob(base64Data: string, contentType: string): Promise<Blob> {
    contentType = contentType || '';

    const dataUri = `data:${contentType};base64,${base64Data}`;

    const response = await fetch(dataUri);
    return response.blob();
}

export function downloadFileFromFilePreview(file: File) {
    if (!file) return;
    if (!file.data) {
        toast.error('FileCantLoad');
        return;
    }

    saveAs(file.data, file.name);
}

export function encodePath(path: string) {
    const encodedPath = encodeURIComponent(path.trim());
    if (encodedPath === '') throw new Error('Path is empty');
    return encodedPath;
}

export async function fileToDataUrl(file: Blob) {
    const reader = new FileReader();

    return await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export function determineFileType(contentType: string): FileType {
    if (contentType.startsWith('image/')) return FileType.Image;
    if (contentType.startsWith('video/')) return FileType.Video;
    if (contentType === 'application/pdf') return FileType.Pdf;
    return FileType.Other;
}

export async function getRawFileData(blob: Blob): Promise<Result> {
    if (blob.type === 'text/plain') return new Result(200, 'Bestandsinhoud uitgelezen', await blob.text());

    const arrayBuffer = await blob.arrayBuffer();
    if (!arrayBuffer) return new Result(422, ErrorMessage.FILE_CANT_EXTRACT_CONTENT, null);

    return new Result(200, 'Bestandsinhoud uitgelezen', new Uint8Array(arrayBuffer));
}
